import { useMemo, useRef } from 'react';

import { useVssQuery } from './useVssQuery';
import { Kitchen } from '../libs';
import { KitchenDoorBucket } from '../views/KitchenFloor/types';
import { listKitchenDoorActivity } from '../views/KitchenFloor/utils';

const DOOR_ACTIVITY_WINDOW_MS = 60 * 60_000;
const DOOR_ACTIVITY_BUCKETS = 12;

/**
 * Door traffic for the last hour, per camera.
 * Loads once for the given `sensorIds` — a room panel or dialog opening with a
 * different set of cameras is a fresh `now`, not a re-poll of the same window.
 */
const useKitchenDoorActivity = (kitchen: Kitchen, sensorIds: string[]): Map<string, KitchenDoorBucket[]> => {
  // Recomputed only when the sensor set itself changes, not on every render —
  // a plain `useMemo` here would drop `sensorIdsKey` as an "unused" dep, since
  // the factory reads the clock rather than the key.
  const sensorIdsKey = sensorIds.join('|');
  const keyRef = useRef(sensorIdsKey);
  const nowRef = useRef(Date.now());
  if (keyRef.current !== sensorIdsKey) {
    keyRef.current = sensorIdsKey;
    nowRef.current = Date.now();
  }
  const now = nowRef.current;

  const { data } = useVssQuery(
    JSON.stringify([kitchen, sensorIds, now]),
    () =>
      Promise.all(
        sensorIds.map((sensorId) =>
          listKitchenDoorActivity(
            kitchen,
            sensorId,
            new Date(now - DOOR_ACTIVITY_WINDOW_MS).toISOString(),
            new Date(now).toISOString(),
            DOOR_ACTIVITY_BUCKETS
          )
        )
      ),
    sensorIds.length === 0
  );

  return useMemo(() => {
    const map = new Map<string, KitchenDoorBucket[]>();
    for (const [index, sensorId] of sensorIds.entries()) {
      map.set(sensorId, data?.[index] ?? []);
    }
    return map;
  }, [sensorIds, data]);
};

export default useKitchenDoorActivity;
